import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Menu,
  MenuItem,
  Checkbox,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  CameraAlt as CameraIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import WebhookIcon from '@mui/icons-material/Webhook';
import { useDispatch, useSelector } from 'react-redux';
import { getToolList, getAgentTools, updateAgentTools } from '../redux/actions/agentToolsActions';
import { showError, showSuccess } from '../Components/reuse/Toast';
import useCallOnce from '../hooks/useCallOnce';

const ToolsSettings = ({ selectedUserId }) => {
  const dispatch = useDispatch();
  const { toolList, agentTools, toolListLoading, agentToolsLoading, updateLoading, updateError, updateSuccess } = useSelector(state => state.agentTools);
  
  const [activeTab, setActiveTab] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTools, setSelectedTools] = useState([]);
  const [userTools, setUserTools] = useState([]);

  // Load user's current tools only once per user arrival using useCallOnce
  useCallOnce(
    `agent-tools:${selectedUserId || 'unknown'}`,
    () => {
      if (!selectedUserId) return;
      const type = activeTab === 0 ? 'simpleprompt' : 'voiceprompt';
      const p = dispatch(getAgentTools(selectedUserId, type));
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to load agent tools');
        });
      }
    },
    [selectedUserId]
  );

  // Update userTools when agentTools data changes; keep UI visible even on failure
  useEffect(() => {
    if (Array.isArray(agentTools)) {
      setUserTools(agentTools);
    }
  }, [agentTools]);

  // Fetch tools when tab changes
  useEffect(() => {
    if (selectedUserId) {
      const type = activeTab === 0 ? 'simpleprompt' : 'voiceprompt';
      const p = dispatch(getAgentTools(selectedUserId, type));
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to load agent tools');
        });
      }
    }
  }, [activeTab, selectedUserId, dispatch]);

  // Load available tools when dropdown opens (only once)
  useEffect(() => {
    if (dropdownOpen) {
      const p = dispatch(getToolList());
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to load tools');
        });
      }
    }
  }, [dropdownOpen]); // Remove dispatch and toolList from dependencies

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleAddToolClick = (event) => {
    setDropdownAnchorEl(event.currentTarget);
    setDropdownOpen(true);
    setSelectedTools([]);
    setSearchTerm('');
  };

  const handleCloseDropdown = () => {
    setDropdownOpen(false);
    setDropdownAnchorEl(null);
    setSelectedTools([]);
    setSearchTerm('');
  };

  const handleToolSelect = (tool) => {
    const toolId = tool.tool_id || tool.id || tool._id || tool.toolId;
    const isSelected = selectedTools.some(selected => 
      (selected.tool_id || selected.id || selected._id || selected.toolId) === toolId
    );
    
    if (isSelected) {
      setSelectedTools(selectedTools.filter(selected => 
        (selected.tool_id || selected.id || selected._id || selected.toolId) !== toolId
      ));
    } else {
      setSelectedTools([...selectedTools, tool]);
    }
  };

  const handleAddWebhookTool = async () => {
    if (selectedTools.length === 0) return;

    try {
      const existingToolIds = userTools.map(userTool => 
        userTool.tool_id || userTool.id || userTool._id || userTool.toolId
      ).filter(Boolean);
      
      // Get new tool IDs from selectedTools
      const newToolIds = selectedTools.map(tool => 
        tool.id || tool.tool_id || tool._id || tool.toolId
      ).filter(Boolean);
      
      // Combine existing and new tool IDs (remove duplicates)
      const allToolIds = [...new Set([...existingToolIds, ...newToolIds])];
      
      await dispatch(updateAgentTools(selectedUserId, allToolIds));
      showSuccess('Tools updated');
      
      // Close dropdown immediately after successful update
      handleCloseDropdown();
      
      // Refresh user's tools after successful update
      const type = activeTab === 0 ? 'simpleprompt' : 'voiceprompt';
      const p = dispatch(getAgentTools(selectedUserId, type));
      if (p && typeof p.catch === 'function') {
        p.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to refresh tools');
        });
      }
    } catch (error) {
      showError(error?.response?.data?.message || error.message || 'Failed to update tools');
    }
  };

  // Remove a single tool from user's tools
  const handleRemoveTool = async (tool) => {
    try {
      const removeId = tool.tool_id || tool.id || tool._id || tool.toolId;
      const remainingIds = userTools
        .map((t) => t.tool_id || t.id || t._id || t.toolId)
        .filter(Boolean)
        .filter((id) => id !== removeId);

      await dispatch(updateAgentTools(selectedUserId, remainingIds));

      // Refresh lists after removal
      const type = activeTab === 0 ? 'simpleprompt' : 'voiceprompt';
      const p1 = dispatch(getAgentTools(selectedUserId, type));
      if (p1 && typeof p1.catch === 'function') {
        p1.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to refresh tools');
        });
      }
      const p2 = dispatch(getToolList());
      if (p2 && typeof p2.catch === 'function') {
        p2.catch((err) => {
          showError(err?.response?.data?.message || err.message || 'Failed to refresh tool list');
        });
      }
    } catch (error) {
      console.error('Error removing agent tool:', error);
    }
  };

  // Show ALL tools from getToolList - NO FILTERING for now
  const allAvailableTools = Array.isArray(toolList) ? toolList : (toolList ? [toolList] : []);
  
  // Filter tools by search term (matches name or description, case-insensitive)
  const filteredTools = allAvailableTools.filter((tool) => {
    const q = (searchTerm || '').trim().toLowerCase();
    if (!q) return true;
    const name = (tool.tool_config?.name || tool.name || '').toLowerCase();
    const desc = (tool.tool_config?.description || tool.description || '').toLowerCase();
    return name.includes(q) || desc.includes(q);
  });

  const isToolSelected = (tool) => {
    const toolId = tool.tool_id || tool.id || tool._id || tool.toolId;
    return selectedTools.some(selected => 
      (selected.tool_id || selected.id || selected._id || selected.toolId) === toolId
    );
  };

  const isToolInUserTools = (tool) => {
    return userTools.some(userTool => {
      // Compare different possible ID fields
      const isMatch = userTool.tool_id === tool.id ||
                     userTool.id === tool.id ||
                     userTool._id === tool.id ||
                     userTool.toolId === tool.id ||
                     (userTool.tool_config?.name || userTool.name) === (tool.tool_config?.name || tool.name);
      
      return isMatch;
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Tabs */}
      <Paper sx={{ mb: 2, border: '1px solid #e5e7eb' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="tools tabs"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '15px',
              fontWeight: 500,
            },
          }}
        >
          <Tab label="Simple Prompt" />
          <Tab label="Voice Prompt" />
        </Tabs>
      </Paper>

      {/* User's Current Tools */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Current Tools
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddToolClick}
            sx={{ borderRadius: 2 }}
          >
            Add tool
          </Button>
        </Box>
        {agentToolsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : userTools.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tools assigned yet. Click "Add tool" to get started.
          </Typography>
        ) : (
          <List>
            {userTools.map((tool, index) => (
              <ListItem key={tool.tool_id || tool.id || tool._id || index} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: '#f4f5f7',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <WebhookIcon sx={{ color: '#9aa0a6' }} fontSize="small" />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={tool.name || tool.toolName || 'Unnamed Tool'}
                  secondary={tool.description || tool.toolDescription || 'No description available'}
                  primaryTypographyProps={{ fontWeight: 500 }}
                  sx={{ ml: 1.5 }}
                />
                <IconButton
                  edge="end"
                  aria-label="remove"
                  onClick={() => handleRemoveTool(tool)}
                  disabled={updateLoading}
                  size="small"
                  sx={{
                    ml: 1,
                    width: 28,
                    height: 28,
                    color: 'error.main',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'error.light',
                      color: '#fff',
                    },
                  }}
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}
      </Paper>

      {/* Add Tool Dropdown */}
      <Menu
        anchorEl={dropdownAnchorEl}
        open={dropdownOpen}
        onClose={handleCloseDropdown}
        PaperProps={{
          sx: { 
            width: 400,
            maxHeight: 500,
            borderRadius: 2
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ p: 2 }}>
          {/* Search Bar */}
          <TextField
            fullWidth
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {/* Selected Tools Chips */}
          {selectedTools.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Selected Tools ({selectedTools.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedTools.map((tool) => (
                  <Chip
                    key={tool.id || tool.tool_id || tool._id || tool.toolId}
                    label={tool.tool_config?.name || tool.name || tool.toolName || 'Unnamed Tool'}
                    onDelete={() => handleToolSelect(tool)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />


          {/* Available Tools List */}
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Available Tools
          </Typography>
          
          {toolListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredTools.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              {!Array.isArray(toolList) || toolList.length === 0 
                ? 'Loading tools...' 
                : 'No tools available from getToolList API.'}
            </Typography>
          ) : (
            <Box>
              {filteredTools.map((tool, index) => {
                const isSelected = isToolSelected(tool);
                const isInUserTools = isToolInUserTools(tool);
                
                return (
                  <MenuItem
                    key={tool.id || tool.tool_id || index}
                    onClick={() => !isInUserTools && handleToolSelect(tool)}
                    disabled={isInUserTools}
                    sx={{
                      backgroundColor: isSelected ? 'grey.200' : 'transparent',
                      opacity: isInUserTools ? 0.6 : 1,
                      '&:hover': {
                        backgroundColor: isSelected ? 'grey.200' : 'grey.100',
                      },
                    }}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isInUserTools}
                      sx={{ mr: 1 , px: 0 }}
                    />
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: '#f4f5f7',
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <WebhookIcon sx={{ color: '#9aa0a6' }} fontSize="small" />
                    </Box>
                    <ListItemText
                      primary={tool.tool_config?.name || tool.name || 'Unnamed Tool'}
                      secondary={(tool.tool_config?.description || tool.description || 'No description available').substring(0, 50) + '...'}
                      primaryTypographyProps={{ 
                        fontWeight: isSelected ? 600 : 400,
                        fontSize: '0.875rem'
                      }}
                      secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      sx={{ ml: 1 }}
                    />
                  </MenuItem>
                );
              })}
            </Box>
          )}

          {updateError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateError}
            </Alert>
          )}
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
            <Button onClick={handleCloseDropdown} color="inherit" size="small">
              Cancel
            </Button>
            <Button
              onClick={handleAddWebhookTool}
              variant="contained"
              disabled={selectedTools.length === 0 || updateLoading}
              startIcon={updateLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              size="small"
            >
              {updateLoading ? 'Adding...' : 'Add webhook tool'}
            </Button>
          </Box>
        </Box>
      </Menu>
    </Box>
  );
};

export default ToolsSettings;
